import { useState, useCallback, useMemo } from 'react';
import { Plus, Mail, Phone, Star } from 'lucide-react';
import { type EntityType, type EntityLink } from '@peles/shared';
import { EntityList, StatusBadge } from '../common';
import { EntityModal } from '../common/EntityModal';
import type { EntityListColumn } from '../common';
import {
  useEntityLinks,
  useCreateEntityLink,
  useDeleteEntityLink,
} from '../../hooks/useEntityLinks';
import { useUsers } from '../../hooks/useUsers';
import { UserSearchableSelect } from '../users/UserSearchableSelect';
import { PhoneLink } from '../common/PhoneLink';

export interface ContactItem {
  id: string;
  linkId: string;
  userId: string;
  userName: string;
  userEmail: string;
  title: string;
  isPrimary: boolean;
  sortOrder: number;
  phone?: string | null;
}

interface ContactListViewProps {
  /** Entity type (EVENT, VENUE, VENDOR) */
  entityType: EntityType;
  /** Entity ID */
  entityId: string;
  /** Label for the list */
  title?: string;
}

export function ContactListView({
  entityType,
  entityId,
  title = 'Contacts',
}: ContactListViewProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [previewContact, setPreviewContact] = useState<ContactItem | null>(null);

  const { data: links = [], isLoading } = useEntityLinks({
    sourceEntityType: entityType,
    sourceEntityId: entityId,
    linkType: 'CONTACT',
  });
  const createLink = useCreateEntityLink();
  const deleteLink = useDeleteEntityLink();
  const { data: users = [] } = useUsers({ isActive: true });

  // Map EntityLinks to ContactItems
  const contacts: ContactItem[] = useMemo(
    () =>
      links.map((link: EntityLink) => {
        const meta = (link.metadata || {}) as Record<string, unknown>;
        return {
          id: link.targetEntityId,
          linkId: link.id,
          userId: link.targetEntityId,
          userName: link.targetEntityName || 'Unknown',
          userEmail: '', // resolved below
          title: (meta.title as string) || '',
          isPrimary: (meta.isPrimary as boolean) || false,
          sortOrder: link.sortOrder,
        };
      }),
    [links],
  );

  // Enrich with user email from users list
  const enrichedContacts = useMemo(() => {
    const userMap = new Map(users.map((u) => [u.id, u]));
    return contacts.map((c) => ({
      ...c,
      userEmail: userMap.get(c.userId)?.email || '',
      phone: userMap.get(c.userId)?.phone || null,
    }));
  }, [contacts, users]);

  const handleAdd = useCallback(() => {
    if (!newUserId || !newTitle.trim()) return;
    createLink.mutate({
      sourceEntityType: entityType,
      sourceEntityId: entityId,
      targetEntityType: 'USER' as EntityType,
      targetEntityId: newUserId,
      linkType: 'CONTACT',
      metadata: { title: newTitle.trim(), isPrimary: false },
    });
    setNewUserId('');
    setNewTitle('');
    setShowAddForm(false);
  }, [newUserId, newTitle, entityType, entityId, createLink]);

  const handleRemove = useCallback(
    (linkId: string) => {
      deleteLink.mutate(linkId);
    },
    [deleteLink],
  );

  const columns: EntityListColumn<ContactItem>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (item) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-primary-700">
              {item.userName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-gray-900 font-medium text-sm">{item.userName}</p>
            {item.userEmail && <p className="text-xs text-gray-500">{item.userEmail}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'title',
      header: 'Role',
      render: (item) => (
        <div className="flex items-center gap-1.5">
          <StatusBadge label={item.title} color="blue" />
          {item.isPrimary && <Star size={12} className="text-amber-500 fill-amber-500" />}
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (item) =>
        item.userEmail ? (
          <a
            href={`mailto:${item.userEmail}`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            <Mail size={12} />
            {item.userEmail}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
      hideOnMobile: true,
    },
  ];

  return (
    <>
      <EntityList
        items={enrichedContacts}
        columns={columns}
        getKey={(item) => item.linkId}
        isLoading={isLoading}
        onPreview={(item) => setPreviewContact(item)}
        title={title}
        titleActions={
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={16} />
            Add Contact
          </button>
        }
        emptyMessage="No contacts assigned"
      />

      {/* Inline Add Form */}
      {showAddForm && (
        <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Person</label>
              <UserSearchableSelect
                value={newUserId}
                onChange={(v) => setNewUserId(typeof v === 'string' ? v : '')}
                placeholder="Search contacts..."
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Role / Title</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Bride, Groom, Coordinator"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!newUserId || !newTitle.trim() || createLink.isPending}
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <EntityModal
        open={previewContact !== null}
        onClose={() => setPreviewContact(null)}
        title={previewContact?.userName || 'Contact Details'}
        size="sm"
        footer={
          previewContact ? (
            <button
              onClick={() => {
                handleRemove(previewContact.linkId);
                setPreviewContact(null);
              }}
              className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            >
              Remove Contact
            </button>
          ) : undefined
        }
      >
        {previewContact && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-xl font-bold text-primary-700">
                  {previewContact.userName.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">{previewContact.userName}</p>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge label={previewContact.title} color="blue" />
                  {previewContact.isPrimary && (
                    <span className="text-xs text-amber-600 flex items-center gap-0.5">
                      <Star size={10} className="fill-amber-500" /> Primary
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2 pt-2">
              {previewContact.userEmail && (
                <a
                  href={`mailto:${previewContact.userEmail}`}
                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                >
                  <Mail size={14} />
                  {previewContact.userEmail}
                </a>
              )}
              {previewContact.phone && (
                <PhoneLink
                  phone={previewContact.phone}
                  icon={<Phone size={14} />}
                  className="gap-2 text-sm text-primary-600 hover:text-primary-700"
                />
              )}
            </div>
          </div>
        )}
      </EntityModal>
    </>
  );
}
